import InlineText from '../../InlineEdit/InlineText';
import { useIntl } from 'react-intl';
import messages from './messages';
import { imageScaleUrl } from '../../../helpers/imageScales';
import './DrinksSection.scss';

const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;

const View = (props: any) => {
  const intl = useIntl();
  const { data, isEditMode, block, onChangeBlock, imageSlot } = props;
  const {
    eyebrow = isEditMode ? '' : intl.formatMessage(messages.defaultEyebrow),
    title = isEditMode ? '' : intl.formatMessage(messages.defaultTitle),
    count_label = isEditMode
      ? ''
      : intl.formatMessage(messages.defaultCountLabel),
    show_allergen_notice,
    subcategories = [],
  } = data;

  const update = (field: string, value: string) =>
    onChangeBlock(block, { ...data, [field]: value });

  const updateCategory = (idx: number, patch: Record<string, any>) => {
    const next = subcategories.map((c: any, i: number) =>
      i === idx ? { ...c, ...patch } : c,
    );
    onChangeBlock(block, { ...data, subcategories: next });
  };

  const addCategory = () => {
    const next = [
      ...subcategories,
      { '@id': newId(), title: '', full_width: false, items: [] },
    ];
    onChangeBlock(block, { ...data, subcategories: next });
  };

  const removeCategory = (idx: number) => {
    const next = subcategories.filter((_: any, i: number) => i !== idx);
    onChangeBlock(block, { ...data, subcategories: next });
  };

  const updateDrink = (
    catIdx: number,
    drinkIdx: number,
    patch: Record<string, any>,
  ) => {
    const cat = subcategories[catIdx];
    const newItems = (cat.items || []).map((d: any, j: number) =>
      j === drinkIdx ? { ...d, ...patch } : d,
    );
    updateCategory(catIdx, { items: newItems });
  };

  const addDrink = (catIdx: number) => {
    const cat = subcategories[catIdx];
    const newItems = [
      ...(cat.items || []),
      { '@id': newId(), name: '', price: '', subtitle: '' },
    ];
    updateCategory(catIdx, { items: newItems });
  };

  const removeDrink = (catIdx: number, drinkIdx: number) => {
    const cat = subcategories[catIdx];
    const newItems = (cat.items || []).filter(
      (_: any, j: number) => j !== drinkIdx,
    );
    updateCategory(catIdx, { items: newItems });
  };

  return (
    <article className="menu-section drinks-section" id="boissons">
      <div className="shell">
        <div className="menu-section-head">
          <div>
            <p className="eyebrow">
              {isEditMode ? (
                <InlineText
                  value={eyebrow}
                  onChange={(v) => update('eyebrow', v)}
                  renderTag="span"
                  placeholder={intl.formatMessage(messages.eyebrowPlaceholder)}
                />
              ) : (
                eyebrow
              )}
            </p>
            <h2>
              {isEditMode ? (
                <InlineText
                  value={title}
                  onChange={(v) => update('title', v)}
                  renderTag="span"
                  placeholder={intl.formatMessage(messages.titlePlaceholder)}
                />
              ) : (
                title
              )}
            </h2>
          </div>
          {(isEditMode || count_label) && (
            <span className="count">
              {isEditMode ? (
                <InlineText
                  value={count_label}
                  onChange={(v) => update('count_label', v)}
                  renderTag="span"
                  placeholder={intl.formatMessage(
                    messages.countLabelPlaceholder,
                  )}
                />
              ) : (
                count_label
              )}
            </span>
          )}
        </div>

        {subcategories.length > 0 ? (
          <div className="drinks-grid">
            {subcategories.map((cat: any, i: number) => {
              const catImg =
                Array.isArray(cat.image) && cat.image.length > 0
                  ? imageScaleUrl(cat.image[0], 'large')
                  : null;
              const catImgElement = catImg ? (
                <img src={catImg} alt={cat.title || ''} loading="lazy" />
              ) : null;
              const catImgSlotted = imageSlot?.({
                fieldId: 'subcategories',
                index: i,
                value: cat.image,
                element: catImgElement,
              });
              const showCatImage = Boolean(catImg) || Boolean(catImgSlotted);
              const categoryClassName = `drink-block inline-host${
                cat.full_width ? ' is-wide' : ''
              }`;
              return (
                <div className={categoryClassName} key={cat['@id'] ?? i}>
                  {showCatImage && (
                    <div
                      className={`drink-block-img${catImg ? '' : ' is-empty'}`}
                    >
                      {catImgSlotted ?? catImgElement}
                    </div>
                  )}
                  <div className="drink-block-head">
                    <h3>
                      {isEditMode ? (
                        <InlineText
                          value={cat.title ?? ''}
                          onChange={(v) => updateCategory(i, { title: v })}
                          renderTag="span"
                          placeholder={intl.formatMessage(
                            messages.categoryPlaceholder,
                          )}
                        />
                      ) : (
                        cat.title
                      )}
                    </h3>
                    {isEditMode && (
                      <button
                        type="button"
                        className="inline-remove"
                        aria-label={intl.formatMessage(
                          messages.removeCategory,
                          { title: cat.title || '' },
                        )}
                        onClick={() => removeCategory(i)}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  {(cat.items || []).length > 0 && (
                    <ul className="drink-list">
                      {(cat.items || []).map((drink: any, j: number) => (
                        <li key={drink['@id'] ?? j} className="inline-host">
                          <span className="drink-line">
                            <span className="name">
                              {isEditMode ? (
                                <InlineText
                                  value={drink.name ?? ''}
                                  onChange={(v) =>
                                    updateDrink(i, j, { name: v })
                                  }
                                  renderTag="span"
                                  placeholder={intl.formatMessage(
                                    messages.drinkNamePlaceholder,
                                  )}
                                />
                              ) : (
                                drink.name
                              )}
                            </span>
                            {(isEditMode || drink.subtitle) && (
                              <span className="sub">
                                {isEditMode ? (
                                  <InlineText
                                    value={drink.subtitle ?? ''}
                                    onChange={(v) =>
                                      updateDrink(i, j, { subtitle: v })
                                    }
                                    renderTag="span"
                                    placeholder={intl.formatMessage(
                                      messages.drinkSubtitlePlaceholder,
                                    )}
                                  />
                                ) : (
                                  drink.subtitle
                                )}
                              </span>
                            )}
                          </span>
                          <span className="px">
                            {isEditMode ? (
                              <InlineText
                                value={drink.price ?? ''}
                                onChange={(v) =>
                                  updateDrink(i, j, { price: v })
                                }
                                renderTag="span"
                                placeholder={intl.formatMessage(
                                  messages.pricePlaceholder,
                                )}
                                ariaLabel={intl.formatMessage(
                                  messages.priceAriaLabel,
                                )}
                              />
                            ) : (
                              drink.price
                            )}
                          </span>
                          {isEditMode && (
                            <button
                              type="button"
                              className="inline-remove"
                              aria-label={intl.formatMessage(
                                messages.removeDrink,
                                {
                                  title:
                                    drink.name ||
                                    intl.formatMessage(
                                      messages.removeDrinkFallback,
                                    ),
                                },
                              )}
                              onClick={() => removeDrink(i, j)}
                            >
                              ✕
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  {isEditMode && (
                    <button
                      type="button"
                      className="inline-add inline-add--full"
                      onClick={() => addDrink(i)}
                    >
                      {intl.formatMessage(messages.addDrinkButton)}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          isEditMode && (
            <p
              style={{
                color: 'var(--brume)',
                fontStyle: 'italic',
                margin: '0 0 var(--space-4)',
              }}
            >
              {intl.formatMessage(messages.emptyState)}
            </p>
          )
        )}

        {isEditMode && (
          <button
            type="button"
            className="inline-add inline-add--full drinks-add-cat"
            onClick={addCategory}
          >
            {intl.formatMessage(messages.addCategoryButton)}
          </button>
        )}

        {show_allergen_notice && (
          <p className="allergie">
            Certains de nos plats contiennent des allergènes. Merci de signaler
            toute allergie ou intolérance à votre serveur avant de commander.
          </p>
        )}
      </div>
    </article>
  );
};

export default View;
